import styled from 'styled-components';
import { DebugFilterRenderAdapter, ILSFilterRenderAdapter } from 'flow-doctor-lib/constants/FilterRenderAdapter';
import { CANDY_APPLE_LIGHT } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import AcceptedStatusInfo from 'flow-doctor-lib/components/filters/AcceptedStatusInfo';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DetailsContainer = styled.div.withConfig({
  displayName: "BranchRenderAdapter__DetailsContainer",
  componentId: "lpuhil-0"
})(["display:flex;justify-content:space-between;"]);

/**
 * Provides a common interface between regular filter branches and debug filter branches.
 * This abstracts any conditional logic from the presentational layer in `WalmartFilterBranchRenderer`
 * and delegates it to subclasses here
 */

export class FilterBranchRenderAdapter {
  constructor(filterBranch) {
    this.filterBranch = filterBranch;
  }
  getFilterBranchOperator() {
    return this.filterBranch.filterBranchOperator;
  }
  getIsAssociationBranch() {
    return this.filterBranch.filterBranchType === 'ASSOCIATION';
  }
  getRenderableFilterBranches() {
    return this.filterBranch.filterBranches.map(b => new FilterBranchRenderAdapter(b));
  }
  getRenderableFilters() {
    return this.filterBranch.filters.map(f => new ILSFilterRenderAdapter(f));
  }
  getAssociationDisplayMode() {
    if (!this.getIsAssociationBranch()) {
      return 'N/A';
    }
    return 'OBJECT_TYPE';
  }
  getObjectTypeIdForInnerFilterBranch(outerObjectTypeId, innerFilterBranch) {
    if (innerFilterBranch.getIsAssociationBranch()) {
      return innerFilterBranch.getObjectTypeId();
    }
    return outerObjectTypeId;
  }
  getObjectTypeId() {
    if (this.getIsAssociationBranch()) {
      return this.filterBranch.objectTypeId;
    }
    return undefined;
  }
  getObjectId() {
    return undefined;
  }
  getBackgroundColor() {
    return undefined;
  }
  renderDetailsComponent(index) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "flow-doctor-lib.flows.diagnosticWizard.enrollmentAnalysis.filterEvals.filterGroup",
      options: {
        index
      }
    });
  }
}

// -------

export class DebugFilterBranchRenderAdapter {
  constructor(filterBranch, options = {
    displayMode: 'ONLY_ACCEPTED'
  }) {
    this.filterBranch = filterBranch;
    this.options = options;
  }
  getDebugBranch() {
    return this.filterBranch;
  }
  getDebugAssociationBranch() {
    return this.filterBranch;
  }
  getIsRejectedAssociationEvalWithNoObjects() {
    if (!this.getIsAssociationBranch()) {
      return false;
    }
    return this.getDebugAssociationBranch().acceptedStatus === 'REJECTED' && this.getDebugAssociationBranch().rejectedAssociationResults.length === 0;
  }
  hasAssociatedObjectEvals() {
    if (this.getIsAssociationBranch()) {
      return false;
    }
    return this.getDebugBranch().filterBranches.length > 0 && this.getDebugBranch().filterBranches[0].type === 'DebugAssociationFilterBranchEvaluationResult';
  }
  getRawRenderableFilters() {
    // Special case when there are no associated objects and we reject the eval
    if (this.getIsRejectedAssociationEvalWithNoObjects() && this.options.displayMode !== 'ONLY_ACCEPTED') {
      var _this$options$origina, _this$options$origina2;
      return (_this$options$origina = (_this$options$origina2 = this.options.originalAssociationFilterMappings) === null || _this$options$origina2 === void 0 || (_this$options$origina2 = _this$options$origina2[this.filterBranch.index]) === null || _this$options$origina2 === void 0 ? void 0 : _this$options$origina2[this.getObjectTypeId()]) !== null && _this$options$origina !== void 0 ? _this$options$origina : [];
    }

    // Association debug branches don't have filters, they only have filterBranches
    return this.getDebugBranch().type === 'DebugFilterBranchEvaluationResult' ? this.options.displayMode !== 'ONLY_ACCEPTED' ? this.getDebugBranch().filters : this.getDebugBranch().acceptedFilters : [];
  }
  getRawRenderableFilterBranches() {
    if (this.getIsAssociationBranch()) {
      return this.options.displayMode !== 'ONLY_ACCEPTED' ? this.getDebugAssociationBranch().associationResults : this.getDebugAssociationBranch().acceptedAssociationResults;
    }
    if (this.options.displayMode !== 'ONLY_ACCEPTED') {
      if (this.getDebugBranch().filterBranchOperator === 'AND' && this.hasAssociatedObjectEvals()) {
        return this.getDebugBranch().filterBranches;
      }
      return this.getDebugBranch().filterBranches;
    }
    return this.getDebugBranch().acceptedFilterBranches;
  }
  getFilterBranchOperator() {
    return this.filterBranch.filterBranchOperator;
  }
  getIsAssociationBranch() {
    return this.filterBranch.type === 'DebugAssociationFilterBranchEvaluationResult';
  }
  getRenderableFilterBranches() {
    return this.getRawRenderableFilterBranches().map(b => new DebugFilterBranchRenderAdapter(b, this.options));
  }
  getRenderableFilters() {
    return this.getRawRenderableFilters().map(f => {
      if ('evaluationData' in f) {
        return new DebugFilterRenderAdapter(f, this.options.displayMode);
      }
      return new ILSFilterRenderAdapter(f);
    });
  }
  getAssociationDisplayMode() {
    if (!this.getIsAssociationBranch()) {
      return 'N/A';
    }
    if (this.getDebugAssociationBranch().acceptedStatus === 'REJECTED' && this.getDebugAssociationBranch().rejectedAssociationResults.length === 0) {
      return 'OBJECT_TYPE';
    }
    return 'OBJECT_ID';
  }
  getObjectTypeIdForInnerFilterBranch(outerObjectTypeId, innerFilterBranch) {
    if (innerFilterBranch.getIsAssociationBranch()) {
      return innerFilterBranch.getObjectTypeId();
    }
    return outerObjectTypeId;
  }
  getObjectTypeId() {
    if (this.getIsAssociationBranch()) {
      return this.getDebugAssociationBranch().objectCoordinates.objectTypeId;
    }
    return undefined;
  }
  getObjectId() {
    if (this.getIsAssociationBranch()) {
      return this.getDebugAssociationBranch().objectCoordinates.objectId;
    }
    return undefined;
  }
  getBackgroundColor() {
    if (this.getIsRejectedAssociationEvalWithNoObjects()) {
      return CANDY_APPLE_LIGHT;
    }
    return undefined;
  }
  renderDetailsComponent(index) {
    return /*#__PURE__*/_jsxs(DetailsContainer, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "flow-doctor-lib.flows.diagnosticWizard.enrollmentAnalysis.filterEvals.filterGroup",
        options: {
          index
        }
      }), /*#__PURE__*/_jsx(AcceptedStatusInfo, {
        acceptedStatus: this.getDebugBranch().acceptedStatus,
        displayMode: this.options.displayMode
      })]
    });
  }
}